import {
  CheckIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
} from "@phosphor-icons/react";
import { Button, CardProps, Flex, Space, Spin, Typography } from "antd";
import { Carousel } from "components/Carousel";
import { useAntTheme } from "hooks/useAntTheme";
import {
  ContactResponse,
  PresenterProfileResponse,
  VoiceProfileResponse,
} from "../types";
import { CanAccess, useOne, useTranslate } from "@refinedev/core";
import { HoverableCard } from "components/HoverableCard";
import { SliderVoicePreview } from "./ContactWithPresenter/SliderVoicePreview";
import { SpeakerSelectProps } from "../projects/components/SpeakerView";
import { DeleteButton } from "@refinedev/antd";
import { useVoicePreviewPlayback } from "../hooks/useVoicePreviewPlayback";
import { useSession } from "hooks/useSession/";
import { radius } from "@libertify/ui";
const CARD_MAX_WIDTH = 345;
const DEFAULT_SLIDES_TO_SHOW = 4;
const DEFAULT_SLIDES_TO_SCROLL = 1;

interface SpeakerCarouselList extends SpeakerSelectProps {
  delete?: boolean;
  onEditContact?: (contact: ContactResponse) => void;
  loading?: boolean;
}

export const SpeakerCarouselList: React.FC<SpeakerCarouselList> = ({
  contacts,
  value,
  loading,
  slidesToShow,
  slidesToScroll,
  onClick,
  delete: canDelete,
  onEditContact,
}) => {
  const { theme } = useAntTheme();
  const { getVoiceForCard, handleVoiceChange } = useVoicePreviewPlayback();

  return (
    <Carousel
      loading={loading}
      slidesToShow={slidesToShow ?? DEFAULT_SLIDES_TO_SHOW}
      slidesToScroll={slidesToScroll ?? DEFAULT_SLIDES_TO_SCROLL}
      slideToShowSM={1}
      slideToShowXS={1}
      cardMaxWidth={CARD_MAX_WIDTH}
      style={{ padding: 24 }}
      numberOfElements={contacts.length}
      initialSlide={contacts.findIndex((item) => item.id === value)}
      empty={{
        image: <UserIcon size={100} />,
      }}
    >
      {contacts.map((item) => {
        return (
          <div
            key={item.id}
            style={{ flex: 1, maxWidth: `${CARD_MAX_WIDTH}px` }}
          >
            <Flex
              onClick={() => onClick(item.id)}
              style={{ position: "relative", margin: "20px 10px" }}
            >
              {item.id === value && (
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    position: "absolute",
                    backgroundColor: theme.colorPrimary,
                    width: 40,
                    height: 40,
                    border: `5px solid ${theme.colorBgElevated}`,
                    borderRadius: radius.circle,
                    color: theme.colorBgElevated,
                    right: -10,
                    top: -10,
                    zIndex: 2,
                  }}
                >
                  <CheckIcon size={22} />
                </Flex>
              )}
              <SpeakerPreview
                onClickVoice={handleVoiceChange(item.id)}
                currentlyPlayedVoice={getVoiceForCard(item.id)}
                selected={item.id === value}
                contact={item}
                style={{ flex: "unset" }}
                delete={canDelete}
                onEditContact={onEditContact}
              />
            </Flex>
          </div>
        );
      })}
    </Carousel>
  );
};

interface SpeakerPreviewProps extends CardProps {
  contact: ContactResponse;
  selected?: boolean;
  currentlyPlayedVoice?: VoiceProfileResponse;
  onClickVoice?: (voice?: VoiceProfileResponse) => void;
  delete?: boolean;
  onEditContact?: (contact: ContactResponse) => void;
}

const SpeakerPreview: React.FC<SpeakerPreviewProps> = (props) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { currentOrganization: organization } = useSession();
  const { data, isLoading } = useOne<PresenterProfileResponse>({
    resource: `media/${organization?.id}/presenter_profiles`,
    id: props.contact.presenter_id,
    queryOptions: {
      enabled: !!props.contact.presenter_id && !!organization?.id,
    },
  });

  const faceProfile = data?.data.face_profile;
  const voiceProfile = data?.data.voice_profile;
  const contactName = `${props.contact.firstname} ${props.contact.lastname}`;
  const picture =
    faceProfile?.thumbnail_asset_path ??
    faceProfile?.custom_asset_path ??
    faceProfile?.extracted_asset_path;
  return (
    <HoverableCard
      hoverable
      style={{
        filter: theme.filterDropShadow,
        width: "100%",
        height: "fit-content",
        justifyContent: "center",
        ...props.style,
        backgroundColor: props.selected
          ? theme.colorPrimary
          : theme.colorBgBase,
      }}
      styles={{
        body: {
          padding: 15,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
        },
      }}
    >
      <Flex
        justify="flex-end"
        style={{ width: "100%", marginRight: 10 }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Space>
          {props.onEditContact && (
            <Button
              type="text"
              size="large"
              icon={<PencilIcon size={23} color={theme.colorTextBase} />}
              aria-label={t(
                "components.templates.settings.presenterTab.editAvatarButton"
              )}
              onClick={() => props.onEditContact?.(props.contact)}
            />
          )}
          {props.delete && (
            <CanAccess resource="media_contacts" action="delete">
              <DeleteButton
                size="large"
                icon={
                  <TrashIcon size={23} color={theme.colorTextDescription} />
                }
                hideText
                resource={`media/${organization?.id}/contacts`}
                recordItemId={props.contact.id}
                accessControl={{ enabled: false }}
                style={{
                  color: theme.colorTextBase,
                  border: "none",
                  boxShadow: "none",
                  background: "transparent",
                }}
              />
            </CanAccess>
          )}
        </Space>
      </Flex>
      <Typography.Text
        ellipsis
        type="secondary"
        style={{
          fontSize: 24,
          textAlign: "center",
          display: "block",
          color: props.selected ? "white" : theme.colorSecondary,
        }}
      >
        {contactName}
      </Typography.Text>
      {picture ? (
        <div
          style={{
            borderRadius: radius.box,
            width: "100%",
            aspectRatio: "1 / 1",
            maxWidth: 200,
            margin: props.delete ? "10px 0 10px 0" : "32px 0 19px 0",
            background: "white",
            backgroundImage: `url(${picture})`,
            backgroundSize: "cover",
          }}
        />
      ) : (
        <div
          style={{
            borderRadius: radius.box,
            width: "100%",
            aspectRatio: "1 / 1",
            maxWidth: 200,
            margin: props.delete ? "10px 0 10px 0" : "32px 0 19px 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isLoading ? (
            <Spin size="large" />
          ) : (
            !picture && (
              <Typography.Text type="secondary">
                {t("projects.components.ProjectCreateWizard.voiceOnly")}
              </Typography.Text>
            )
          )}
        </div>
      )}
      {voiceProfile && (
        <SliderVoicePreview
          voice={voiceProfile}
          currentlyPlayedVoice={props.currentlyPlayedVoice}
          onClickPlay={props.onClickVoice}
          organizationId={organization?.id}
          style={{
            mixBlendMode: props.selected ? "plus-lighter" : "normal",
          }}
        />
      )}
    </HoverableCard>
  );
};
